import { useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useGames } from '@/features/games/games-queries';
import {
  PLATFORMS,
  Platform,
  CreateMaintenanceInput,
  MaintenanceWindow,
  UpdateMaintenanceInput,
} from './maintenance-types';
import { MaintenanceForm } from './maintenance-form';
import {
  useCreateMaintenance,
  useDeleteMaintenance,
  useMaintenance,
  useUpdateMaintenance,
} from './maintenance-queries';
import { formatDate } from '@/lib/format';

export function MaintenancePage(): JSX.Element {
  const { data: games } = useGames();
  const [filterGameId, setFilterGameId] = useState<string>('');
  const [filterPlatform, setFilterPlatform] = useState<Platform | ''>('');
  const { data: windows, isLoading, error } = useMaintenance({
    game_id: filterGameId || undefined,
    platform: filterPlatform || undefined,
  });
  const createMut = useCreateMaintenance();
  const updateMut = useUpdateMaintenance();
  const deleteMut = useDeleteMaintenance();

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<MaintenanceWindow | null>(null);
  const [deleting, setDeleting] = useState<MaintenanceWindow | null>(null);

  function gameName(id: string): string {
    return games?.find((g) => g.id === id)?.package_name ?? id.slice(0, 8);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">Maintenance</h2>
          <p className="text-sm text-muted-foreground">
            Schedule maintenance windows. 1 row = 1 platform — for a full-client outage, create 4 rows.
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={filterGameId || 'all'} onValueChange={(v) => setFilterGameId(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-56">
              <SelectValue placeholder="All games" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All games</SelectItem>
              {games?.map((g) => (
                <SelectItem key={g.id} value={g.id}>{g.package_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterPlatform || 'all'} onValueChange={(v) => setFilterPlatform(v === 'all' ? '' : (v as Platform))}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder="All platforms" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All platforms</SelectItem>
              {PLATFORMS.map((p) => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" /> New Window
          </Button>
        </div>
      </div>

      {isLoading && <p className="text-muted-foreground">Loading…</p>}
      {error && <p className="text-destructive">Failed to load maintenance windows.</p>}

      {windows && windows.length === 0 && (
        <div className="rounded-lg border border-dashed p-8 text-center">
          <p className="text-muted-foreground">No maintenance windows scheduled.</p>
        </div>
      )}

      {windows && windows.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Game</TableHead>
              <TableHead>Platform</TableHead>
              <TableHead>Window</TableHead>
              <TableHead>Message</TableHead>
              <TableHead>Active</TableHead>
              <TableHead className="w-24">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {windows.map((w) => (
              <TableRow key={w.id}>
                <TableCell className="font-mono text-sm">{gameName(w.game_id)}</TableCell>
                <TableCell className="font-mono text-sm">{w.platform}</TableCell>
                <TableCell className="text-sm">
                  {formatDate(w.starts_at)} → {formatDate(w.ends_at)}
                </TableCell>
                <TableCell className="text-sm max-w-md truncate">{w.message}</TableCell>
                <TableCell>{w.is_active ? '✓' : '—'}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setEditing(w)} aria-label="Edit">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setDeleting(w)} aria-label="Delete">
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Maintenance Window</DialogTitle>
          </DialogHeader>
          {games && (
            <MaintenanceForm
              games={games}
              onSubmit={async (data) => {
                await createMut.mutateAsync(data as CreateMaintenanceInput);
                setCreateOpen(false);
              }}
              onCancel={() => setCreateOpen(false)}
              isSubmitting={createMut.isPending}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Maintenance Window</DialogTitle>
          </DialogHeader>
          {editing && games && (
            <MaintenanceForm
              initial={editing}
              games={games}
              onSubmit={async (data) => {
                await updateMut.mutateAsync({ id: editing.id, input: data as UpdateMaintenanceInput });
                setEditing(null);
              }}
              onCancel={() => setEditing(null)}
              isSubmitting={updateMut.isPending}
            />
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete maintenance window?</AlertDialogTitle>
            <AlertDialogDescription>
              The window will no longer be enforced. Game clients on{' '}
              <code>{deleting?.platform}</code> will resume normal operation.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (deleting) {
                  await deleteMut.mutateAsync(deleting.id);
                  setDeleting(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
